import { getInstance } from '../../../common/api/spidertracks-sdk';
import { PAGE_SIZE } from '../../../components/Settings/poi/CustomPoiContainer';
import { StandardThunk } from '../../../store';
import {
  getPoiCodePreference,
  getPoiCreateMinimumRole,
  getSelectedCustomPoiIds,
  getEditingCustomPoi,
  getCustomPoisPageNumber
} from '../../selectors/poiAdmin';
import {
  setCustomPois,
  setCustomPoisTotal,
  setLocationTypes,
  setLocationTypesLoading,
  setLocationTypesLoadingFailed,
  setPoiPreferenceUpdateFailed,
  setPoiPreferenceUpdateSuccess,
  setPoiPreferenceUpdating,
  setPoisDeleteFailed,
  setPoisDeleteSuccess,
  setPoisLoading,
  setPoisLoadingFailed,
  setPoiUpdateFailed,
  setPoiUpdateSuccess,
  setPoiUpdating
} from './poiAdmin';

export const populateCustomPois = (organisationId: string): StandardThunk<void> => async (
  dispatch,
  getState
) => {
  const sdk = getInstance();
  dispatch(setPoisLoading(true));
  const customPoisPageNumber = getCustomPoisPageNumber(getState());
  try {
    const { data: pois, totalItems } = await sdk
      .getPoiService()
      .getCustomPois(organisationId, customPoisPageNumber, PAGE_SIZE);
    dispatch(setCustomPois(pois));
    dispatch(setCustomPoisTotal(totalItems));
  } catch (e) {
    console.error('Error populating custom pois:', e);
    dispatch(setPoisLoadingFailed(true));
  } finally {
    dispatch(setPoisLoading(false));
  }
};

export const populateLocationTypes = (): StandardThunk<void> => async dispatch => {
  const sdk = getInstance();
  dispatch(setLocationTypesLoading(true));
  try {
    const locationTypes = await sdk.getPoiService().getLocationTypes();
    dispatch(setLocationTypes(locationTypes));
  } catch (e) {
    console.error('Error populating location types:', e);
    dispatch(setLocationTypesLoadingFailed(true));
  } finally {
    dispatch(setLocationTypesLoading(false));
  }
};

export const updateCustomPoi = (organisationId: string): StandardThunk<void> => async (
  dispatch,
  getState
) => {
  const sdk = getInstance();
  dispatch(setPoiUpdating(true));
  const editingCustomPoi = getEditingCustomPoi(getState());
  if (editingCustomPoi) {
    try {
      await sdk.getPoiService().updateCustomPoi(organisationId, editingCustomPoi);
      dispatch(setPoiUpdateSuccess(true));
    } catch (e) {
      console.error('Error updating custom poi:', e);
      dispatch(setPoiUpdateFailed(true));
    } finally {
      dispatch(setPoiUpdating(false));
    }
  }
};

export const deleteCustomPois = (organisationId: string): StandardThunk<void> => async (
  dispatch,
  getState
) => {
  const sdk = getInstance();
  dispatch(setPoisLoading(true));
  const selectedCustomPoiIds = getSelectedCustomPoiIds(getState());
  const deletePromises = selectedCustomPoiIds.map(id =>
    sdk.getPoiService().deleteCustomPoi(organisationId, id)
  );
  try {
    await Promise.all(deletePromises);
    dispatch(setPoisDeleteSuccess(true));
    dispatch(populateCustomPois(organisationId));
  } catch (e) {
    console.error('Error deleting custom poi:', e);
    dispatch(setPoisDeleteFailed(true));
  } finally {
    dispatch(setPoisLoading(false));
  }
};

export const updatePoiPreferences = (organisationId: string): StandardThunk<void> => async (
  dispatch,
  getState
) => {
  const sdk = getInstance();
  dispatch(setPoiPreferenceUpdating(true));
  const poiCodePreference = getPoiCodePreference(getState());
  const poiCreateMinimumRole = getPoiCreateMinimumRole(getState());
  try {
    if (poiCodePreference && poiCreateMinimumRole) {
      await sdk
        .getPoiService()
        .updatePoiPreferences(organisationId, poiCodePreference, poiCreateMinimumRole);
    }
    dispatch(setPoiPreferenceUpdateSuccess(true));
  } catch (e) {
    console.error('Error updating poi preferences:', e);
    dispatch(setPoiPreferenceUpdateFailed(true));
  } finally {
    dispatch(setPoiPreferenceUpdating(false));
  }
};
